import { useState } from "react";
import ResultModal from "../components/ResultModal.jsx";
import Shimmer from "../components/Shimmer.jsx";
import config from "../config/index.js";
import { useWallet } from "../context/WalletContext.jsx";
import { useDeposits } from "../hooks/useDeposits.js";
import { useGeofence } from "../hooks/useGeofence.js";
import { getContract } from "../services/contracts.js";
import { getTxOverrides } from "../services/gas.js";
import { decodeContractError } from "../utils/contractError.js";
import { formatUsdcCompact, shortAddress } from "../utils/format.js";

const COMPOUND_MODES = [
    { id: 0, label: "Off", hint: "Interest accrues normally — no reinvestment." },
    { id: 1, label: "Standard", hint: "Accrued interest opens a fresh term deposit at 100 USDC." },
    { id: 2, label: "Turbo", hint: "Interest seeds a leveraged Turbo deposit when LTV allows." }
];

/**
 * Settings — account-level configuration.
 *
 * Hosts the spec §17 Automated Reinvestment controls (per-deposit OFF / Standard
 * / Turbo mode selection), the §22.2 jurisdiction / compliance status,
 * and basic wallet + network information.
 */
export default function Settings() {
    const { address, ensName, signer, disconnect } = useWallet();
    const { deposits, isLoading, refresh, patchDeposit } = useDeposits(address);
    const geo = useGeofence();

    const [busyId, setBusyId] = useState(null);
    const [result, setResult] = useState(null);

    const activeDeposits = deposits.filter((d) => Number(d.state) === 1);

    async function handleSetMode(depositId, mode) {
        if (!signer || busyId != null) return;
        setBusyId(depositId);
        try {
            // §I.6 V6.2 — setCompoundMode lives on the BondContract;
            // the standalone AutoCompound contract is deprecated.
            const bond = getContract("bond", signer);
            const overrides = await getTxOverrides(signer.provider);
            const tx = await bond.setCompoundMode(depositId, mode, overrides);
            await tx.wait();
            // Optimistic patch so the UI reflects the new mode immediately.
            // WS listener will overwrite with canonical chain state within ~1s.
            patchDeposit(depositId, { compoundMode: mode });
            setResult({
                type: "success",
                title: "Automated Reinvestment Updated",
                message: `Term Deposit #${depositId} is now set to ${COMPOUND_MODES[mode].label} mode.`
            });
            await refresh();
        } catch (err) {
            setResult({
                type: "error",
                title: "Update Failed",
                message: decodeContractError(err)
            });
        } finally {
            setBusyId(null);
        }
    }

    if (!address) {
        return (
            <div className="-mt-6 flex min-h-[60vh] items-center justify-center">
                <div className="rounded-[14px] bg-[#10281b] ring-1 ring-[#bfb67b] px-8 py-10 text-center max-w-md">
                    <h2 className="font-manrope text-[18px] font-bold text-[#e6c985]">Wallet Required</h2>
                    <p className="mt-2 text-[12px] text-[#e6c985]/80">
                        Connect your wallet to manage your settings.
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-3">
        

            {/* Automated Reinvestment controls */}
            <section className="rounded-[12px] bg-[#10281b] ring-1 ring-[#bfb67b] p-4">
                <div className="flex items-center justify-between">
                    <h3 className="font-manrope text-[12px] font-bold text-[#e6c985]">
                        Automated Reinvestment Preferences
                    </h3>
                    <span className="text-[10px] text-[#e6c985]/65">
                        {activeDeposits.length} active deposit{activeDeposits.length === 1 ? "" : "s"}
                    </span>
                </div>
                <p className="mt-1 text-[10.5px] text-[#e6c985]/70">
                    Choose how each deposit's accrued interest is reinvested once it
                    crosses the 100 {""}
                    USDC trigger. Turbo mode only compounds while LTV headroom allows.
                </p>

                <ul className="mt-3 space-y-2">
                    {isLoading && deposits.length === 0 ? (
                        <li>
                            <Shimmer width="100%" height="4rem" />
                        </li>
                    ) : null}
                    {!isLoading && activeDeposits.length === 0 ? (
                        <li className="rounded-[10px] bg-white/10 ring-1 ring-[#bfb67b] px-3 py-5 text-center text-[11px] text-[#e6c985]/70">
                            No active deposits. Open a term deposit to configure
                            auto-compounding.
                        </li>
                    ) : null}
                    {activeDeposits.map((d) => {
                        const current = Number(d.compoundMode) || 0;
                        return (
                            <li
                                key={d.id}
                                className="rounded-[10px] bg-white/10 ring-1 ring-[#bfb67b] px-3 py-2.5"
                            >
                                <div className="flex items-center justify-between">
                                    <div className="font-manrope text-[12px] font-bold text-white">
                                        Term Deposit #{String(d.id).padStart(6, "0")}
                                    </div>
                                    <div className="text-[10px] text-[#e6c985]/70">
                                        ${formatUsdcCompact(d.principal)} principal
                                    </div>
                                </div>
                                <div className="mt-2 flex flex-wrap gap-1.5">
                                    {COMPOUND_MODES.map((m) => {
                                        const selected = current === m.id;
                                        return (
                                            <button
                                                key={m.id}
                                                type="button"
                                                disabled={busyId != null}
                                                onClick={() => !selected && handleSetMode(d.id, m.id)}
                                                title={m.hint}
                                                className={`rounded-md px-3 py-1.5 text-[11px] font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
                                                    selected
                                                        ? "bg-[#FBC206] text-[#10281b]"
                                                        : "bg-[#162e1f] text-[#e6c985] ring-1 ring-[#bfb67b] hover:bg-[#1d3a27]"
                                                }`}
                                            >
                                                {busyId === d.id && !selected ? "..." : m.label}
                                            </button>
                                        );
                                    })}
                                </div>
                                <p className="mt-1.5 text-[10px] text-[#e6c985]/60">
                                    {COMPOUND_MODES[current].hint}
                                </p>
                            </li>
                        );
                    })}
                </ul>
            </section>

            <div className="grid gap-3 lg:grid-cols-2">
                {/* Jurisdiction / compliance */}
                <section className="rounded-[12px] bg-[#10281b] ring-1 ring-[#bfb67b] p-4">
                    <h3 className="font-manrope text-[12px] font-bold text-[#e6c985]">
                        Region & Compliance
                    </h3>
                    <dl className="mt-3 space-y-2 text-[11px]">
                        <Row label="Detected Region">
                            {geo.isLoading ? (
                                <Shimmer width="3rem" height="0.75rem" />
                            ) : (
                                <span className="font-semibold text-white">
                                    {geo.country || "Unknown"}
                                </span>
                            )}
                        </Row>
                        <Row label="Access Status">
                            <span
                                className={`font-semibold ${
                                    geo.blocked ? "text-[#ff8a8a]" : "text-[#22C55E]"
                                }`}
                            >
                                {geo.blocked ? "Restricted" : "Permitted"}
                            </span>
                        </Row>
                        <Row label="Restricted Regions">
                            <span className="font-semibold text-white">
                                {(config.restrictedRegions || []).join(", ") || "None"}
                            </span>
                        </Row>
                    </dl>
                    <p className="mt-3 text-[10px] leading-snug text-[#e6c985]/60">
                        Access from restricted jurisdictions is geofenced at the
                        application layer for compliance. The protocol smart
                        contracts remain permissionless.
                    </p>
                </section>

                {/* Wallet + network */}
                <section className="rounded-[12px] bg-[#10281b] ring-1 ring-[#bfb67b] p-4">
                    <h3 className="font-manrope text-[12px] font-bold text-[#e6c985]">
                        Wallet & Network
                    </h3>
                    <dl className="mt-3 space-y-2 text-[11px]">
                        <Row label="Address">
                            <span className="font-semibold text-white">
                                {ensName || shortAddress(address)}
                            </span>
                        </Row>
                        <Row label="Network">
                            <span className="font-semibold text-white">
                                {config.network.name}
                            </span>
                        </Row>
                        <Row label="Chain ID">
                            <span className="font-semibold text-white">
                                {config.network.chainId}
                            </span>
                        </Row>
                    </dl>
                    <button
                        type="button"
                        onClick={disconnect}
                        className="mt-3 inline-flex items-center justify-center rounded-md bg-[#162e1f] ring-1 ring-[#bfb67b] px-4 py-2 text-[11px] font-semibold text-[#e6c985] hover:bg-[#1d3a27] transition-colors"
                    >
                        Disconnect Wallet
                    </button>
                </section>
            </div>

            <ResultModal
                open={result !== null}
                type={result?.type}
                title={result?.title || ""}
                message={result?.message || ""}
                onClose={() => setResult(null)}
            />
        </div>
    );
}

function Row({ label, children }) {
    return (
        <div className="flex items-center justify-between border-b border-white/5 pb-1.5 last:border-0">
            <dt className="text-[#e6c985]/70">{label}</dt>
            <dd>{children}</dd>
        </div>
    );
}

function GearIcon() {
    return (
        <svg className="h-3 w-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <circle cx="12" cy="12" r="3" />
            <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.6 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.6a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
        </svg>
    );
}